import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, ArrowLeft } from 'lucide-react';
import BlogList from '../components/BlogList';
import Logo from '../components/Logo';
import Footer from '../components/Footer';
import { BlogPost, Theme } from '../types';

interface BlogPageProps {
  posts: BlogPost[];
  theme: Theme;
  onEditPost?: (post: BlogPost) => void;
  onDeletePost?: (postId: string) => void;
  onLikePost?: (postId: string) => void;
}

export default function BlogPage({ posts, theme, onEditPost, onDeletePost, onLikePost }: BlogPageProps) {
  const navigate = useNavigate();

  return (
    <div className={`min-h-screen ${
      theme === 'dark' 
        ? 'bg-gray-900 text-gray-100' 
        : 'bg-gray-50 text-gray-900'
    }`}>
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate('/')}
            className={`flex items-center gap-2 ${
              theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <ArrowLeft size={20} />
            Back to Dashboard
          </button>
          <div className="flex items-center gap-3">
            <Logo theme={theme} size="small" />
            <h1 className={`text-2xl font-bold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              LinguaTracker Blog
            </h1>
          </div>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/blog/new')}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
              theme === 'dark'
                ? 'bg-purple-600 text-white hover:bg-purple-700'
                : 'bg-purple-600 text-white hover:bg-purple-700'
            }`}
          >
            <Plus size={20} />
            New Post
          </motion.button>
        </div>

        <div className="mb-8">
          <p className={`text-lg ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
          }`}>
            Explore articles about language learning, vocabulary building, and linguistic insights.
          </p>
        </div>

        <BlogList
          posts={posts}
          theme={theme}
          onEdit={onEditPost}
          onDelete={onDeletePost}
          onLike={onLikePost}
        />
      </div>
      <Footer theme={theme} />
    </div>
  );
}